<!-- Component can be used in microscope and periscope -->
<template>
  <!-- Must stay a <span>: chips are rendered inside <button> in the filter bars, which only allows
       phrasing content. The layout comes from `display: inline-flex` below, not from the element. -->
  <span class="chip" :data-color="color" :data-outlined="outlined ? '' : undefined">
    <span v-if="dot" class="dot" aria-hidden="true"></span>
    <slot />
  </span>
</template>

<script setup lang="ts">
import type { ChipColor } from './AppChip.types'

defineProps<{
  color?: ChipColor
  // Optional leading dot in the family accent colour — use to emphasise the status.
  dot?: boolean
  // Border and text in the colour, no fill: for a state that is not a value, e.g. a read that failed.
  outlined?: boolean
}>()
</script>

<style lang="scss" scoped>
// Soft style: light -subtle background, -emphasis border + dot, dark -on-subtle text.
//
// Consumers may override the default (no `color`) surface via the `--chip-background-color` and
// `--chip-text-color` custom properties — set them on an ancestor. Used for the application-type
// chips in periscope's BorgoApplicationsTable. Colour variants below ignore these on purpose.
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  line-height: 1.25rem;
  height: fit-content;
  border: 1px solid transparent;

  // Default (no colour) / consumer override
  background-color: var(--chip-background-color, var(--color-canvas-subtle));
  border-color: var(--color-border-default);
  color: var(--chip-text-color, var(--color-fg-default));

  &[data-color='brand'] {
    background-color: var(--color-brand-subtle);
    border-color: var(--color-brand-default);
    color: var(--color-brand-on-subtle);

    .dot {
      color: var(--color-brand-default);
    }
  }

  &[data-color='success'] {
    background-color: var(--color-success-subtle);
    border-color: var(--color-success-emphasis);
    color: var(--color-success-on-subtle);

    .dot {
      color: var(--color-success-emphasis);
    }
  }

  &[data-color='warning'] {
    background-color: var(--color-warning-subtle);
    border-color: var(--color-warning-emphasis);
    color: var(--color-warning-on-subtle);

    .dot {
      color: var(--color-warning-emphasis);
    }
  }

  &[data-color='danger'] {
    background-color: var(--color-danger-subtle);
    border-color: var(--color-danger-emphasis);
    color: var(--color-danger-on-subtle);

    .dot {
      color: var(--color-danger-emphasis);
    }
  }

  &[data-color='muted'] {
    background-color: var(--color-canvas-subtle);
    border-color: var(--color-border-default);
    color: var(--color-fg-subtle);
  }

  // After the colour variants, so it wins over their fill. Text takes the border's colour.
  &[data-outlined] {
    background-color: transparent;
  }

  &[data-outlined][data-color='danger'] {
    color: var(--color-danger-fg);
  }
}

// Decorative leading dot. Takes the text colour unless a variant overrides it above.
.dot {
  width: 0.5em;
  height: 0.5em;
  flex: none;
  border-radius: 50%;
  background-color: currentColor;
}
</style>
